import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle, ChevronDown, ChevronUp, Menu, Search, TrendingUp, Users, X,
} from "lucide-react";

import { EmptyState, Modal, Progress, SimpleTable, Tabs } from "../../Shared/ScreenComponents.jsx";
import { ENGINEERING_FLAT, resolveRungIndex, ladderFor, growthRungLabel } from "../../Shared/GrowthRoadmapContent.js";
import { apiGet, apiPatch } from "../../Api/Client.js";
import { hasNamedDepartment } from "./hasNamedDepartment.js";
import { byBountyHighestFirst } from "./bountyOrder.js";
import "../../Styles/HrmsModal.css";
import {
  calendarDays, filterForEmployee, findDailyStatus, formatDate, findById,
  getAttendanceStatus, groupBy, indexById, isCompleted, isoDate, lastDays,
  progressForTask, projectName, toggleSet,
} from "../../Shared/ScreenUtils.jsx";

// HRMS — converged 2026-10-06 from a Team / Org Chart two-tab page into one.
// Org Chart is gone: it was the same department roster Team already shows,
// minus the EOD detail. The per-department "Assign Lead / Manager" dropdown
// is gone too, by explicit instruction — the underlying manager relationship
// (used elsewhere, e.g. who gets Leave Requests without the review
// capability) is untouched; only the assignment UI on this screen is
// removed. The page's job now is narrower and more specific than before:
// for each person, how much work they're carrying, and where they sit on
// their growth ladder. Skill-level editing, the free-text remarks box, the
// performance-tag menu and "Send Interview" are cut for the same reason —
// none of them serve either of those two questions, and the point of this
// pass was fewer things to look at, not more.

/* ─── Colour Helpers ─────────────────────────────────────────── */
const AVATAR_COLORS = ["var(--indigo-500)","var(--blue-500)","var(--green-500)","var(--amber-500)","#ec4899","var(--indigo-500)","#14b8a6","var(--amber-600)"];
function avatarColor(name = "") {
  let n = 0;
  for (let i = 0; i < name.length; i += 1) n += name.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}
function initials(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

/* ─── HrmsScreen ─────────────────────────────────────────────── */
export function HrmsScreen({ data, reload }) {
  const [search, setSearch]     = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [eodEmployee, setEodEmployee] = useState(null);

  // Who finished how many tasks and story points. Computed by the server over
  // every task -- this page used to count from the one capped page of tasks the
  // app loads, which held almost nobody's work and so showed zero for nearly
  // everyone.
  const [deliveryDays, setDeliveryDays] = useState(30);
  const [delivery, setDelivery] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setDelivery(null);
    apiGet(`/TasksDashboard/WorkItems/delivery-summary/?days=${deliveryDays}`)
      .then((res) => { if (!cancelled) setDelivery(res || { people: {} }); })
      .catch(() => { if (!cancelled) setDelivery({ people: {}, unavailable: true }); });
    return () => { cancelled = true; };
  }, [deliveryDays]);

  // Load HRMS-Specific Data On Mount
  useEffect(() => {
    if (reload) reload(["employees", "departments", "subDepartments", "positions", "domains", "departmentMemberships", "skills", "userSkills", "payProfiles", "employeeRatings", "dailyStatus", "leaveRequests"]);
  }, []);

  const teamEmployees = (data.employees || []).filter(hasNamedDepartment);
  const employees   = teamEmployees.filter((e) =>
    e.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.department_name?.toLowerCase().includes(search.toLowerCase()),
  );
  // Every department that is not archived appears here, INCLUDING the ones with
  // nobody in them. Grouping employees alone silently hid empty departments —
  // which are exactly the ones you need to see in order to archive them.
  const departments = useMemo(() => {
    const grouped = groupBy(employees, (e) => e.department_name || "Not Assigned");
    if (!search) {
      for (const d of data.activeDepartments || []) {
        if (d.name && !grouped.has(d.name)) grouped.set(d.name, []);
      }
    }
    // Biggest department first -- the one carrying the most people is the
    // one most worth seeing without scrolling. Alphabetical only breaks ties.
    return new Map([...grouped.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0])));
  }, [employees, data.activeDepartments, search]);

  const totalEmp  = teamEmployees.length;
  const deptCount = new Set(teamEmployees.map((e) => e.department_name).filter(Boolean)).size;

  const toggleAll = () =>
    expanded.size === departments.size
      ? setExpanded(new Set())
      : setExpanded(new Set(Array.from(departments.keys())));

  return (
    <section className="Hrms-V2">
      <div className="Hrms-Hero">
        <div className="Hrms-Hero-Left">
          <span className="Hrms-Kicker">Human Resources</span>
          <h1 className="Hrms-Hero-Title">Team</h1>
          <p className="Hrms-Hero-Sub">{totalEmp} people across {deptCount} departments — how much work each person is carrying, and where they sit on their growth ladder.</p>
        </div>
      </div>

      <div className="Hrms-Body">
        <div className="Hrms-Toolbar">
          <div className="Hrms-Search">
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Employees Or Departments…"
            />
          </div>
          <button className="Outline-Button" onClick={toggleAll}>
            <Menu size={14} />
            {expanded.size === departments.size ? "Collapse All" : "Expand All"}
          </button>
        </div>
        <DeliveryBoard
          employees={teamEmployees}
          delivery={delivery}
          days={deliveryDays}
          onDays={setDeliveryDays}
        />
        <div className="Hrms-Dept-Stack">
          {Array.from(departments.entries()).map(([deptName, rows]) => {
            const active = rows.filter((e) => e.status === "Active").length;
            const bench  = rows.filter((e) => e.status === "OnBench").length;
            const isOpen = expanded.has(deptName);
            return (
              <div className="Hrms-Dept-Card" key={deptName}>
                <button
                  className="Hrms-Dept-Head"
                  onClick={() => setExpanded(toggleSet(expanded, deptName))}
                >
                  <div className="Hrms-Dept-Left" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="Hrms-Dept-Icon"><Users size={16} /></span>
                    <strong>{deptName}</strong>
                    <span className="Hrms-Dept-Badge">{rows.length}</span>
                  </div>
                  <div className="Hrms-Dept-Right">
                    {bench > 0 && <span className="Hrms-Pill Slate">On Bench: {bench}</span>}
                    <span className="Hrms-Pill Green">Active: {active}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>
                {isOpen && (
                  <HrmsTeamTable
                    rows={rows}
                    data={data}
                    delivery={delivery}
                    deliveryDays={deliveryDays}
                    setEodEmployee={setEodEmployee}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {eodEmployee && (
        <EodSummaryModal
          employee={eodEmployee}
          data={data}
          reload={reload}
          onClose={() => setEodEmployee(null)}
        />
      )}
    </section>
  );
}

/* ─── DeliveryBoard ──────────────────────────────────────────── */
// One table answering "who is actually finishing work": tasks and story points
// (the bounty on each task) finished in the window, what's still open, and --
// because completion dates only started being recorded recently -- how many
// older finished tasks can't be placed in any window. Showing that last number
// is the difference between "this person delivered nothing" and "this person's
// history has no dates".
const DELIVERY_WINDOWS = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const DELIVERY_ROWS_SHOWN = 12;
const fmtPoints = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function DeliveryBoard({ employees, delivery, days, onDays }) {
  const [showAll, setShowAll] = useState(false);
  const people = delivery?.people || {};

  const rows = useMemo(() => employees
    .map((e) => ({ employee: e, d: people[String(e.id)] }))
    .filter((r) => r.d && (r.d.open_tasks || r.d.done_all_time))
    .sort((a, b) => b.d.done_points - a.d.done_points || b.d.done_tasks - a.d.done_tasks || b.d.open_points - a.d.open_points),
  [employees, people]);

  const totals = rows.reduce((t, r) => ({
    doneTasks: t.doneTasks + r.d.done_tasks, donePoints: t.donePoints + r.d.done_points,
    openTasks: t.openTasks + r.d.open_tasks, openPoints: t.openPoints + r.d.open_points,
  }), { doneTasks: 0, donePoints: 0, openTasks: 0, openPoints: 0 });
  const shown = showAll ? rows : rows.slice(0, DELIVERY_ROWS_SHOWN);

  return (
    <div className="Hrms-Delivery">
      <div className="Hrms-Delivery-Head">
        <div>
          <strong>Delivery</strong>
          <small>Tasks and story points finished, and what is still open — {rows.length} people with work on record</small>
        </div>
        <div className="Hrms-Delivery-Windows">
          {DELIVERY_WINDOWS.map(([n, label]) => (
            <button key={n} type="button" className={"Hrms-Delivery-Win" + (days === n ? " on" : "")} onClick={() => onDays(n)}>{label}</button>
          ))}
        </div>
      </div>

      {delivery === null && <p className="Hrms-Delivery-Note">Loading…</p>}
      {delivery?.unavailable && <p className="Hrms-Delivery-Note">You don't have access to company-wide delivery numbers.</p>}
      {delivery && !delivery.unavailable && !rows.length && <p className="Hrms-Delivery-Note">No tasks on record yet.</p>}

      {rows.length > 0 && (
        <>
          <div className="Hrms-Delivery-Totals">
            <span><b>{totals.doneTasks}</b> tasks / <b>{fmtPoints(totals.donePoints)}</b> pts finished in {days} days</span>
            <span><b>{totals.openTasks}</b> tasks / <b>{fmtPoints(totals.openPoints)}</b> pts still open</span>
          </div>
          <div className="Hrms-Table-Wrap">
            <table className="Hrms-Emp-Table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Department</th>
                  <th className="Hrms-Delivery-Num">Finished ({days}d)</th>
                  <th className="Hrms-Delivery-Num">Story points</th>
                  <th className="Hrms-Delivery-Num">Open</th>
                  <th className="Hrms-Delivery-Num">Open points</th>
                  <th className="Hrms-Delivery-Num" title="Finished before completion dates were recorded, so they can't be placed in any window">Older, undated</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ employee, d }) => (
                  <tr key={employee.id} className="Hrms-Emp-Row">
                    <td><strong>{employee.display_name}</strong></td>
                    <td>{employee.department_name || "—"}</td>
                    <td className="Hrms-Delivery-Num">{d.done_tasks}</td>
                    <td className="Hrms-Delivery-Num"><strong>{fmtPoints(d.done_points)}</strong></td>
                    <td className="Hrms-Delivery-Num">{d.open_tasks}</td>
                    <td className="Hrms-Delivery-Num">{fmtPoints(d.open_points)}</td>
                    <td className="Hrms-Delivery-Num Hrms-Delivery-Dim">{d.done_undated || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > DELIVERY_ROWS_SHOWN && (
            <button type="button" className="Outline-Button Hrms-Delivery-More" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Show fewer" : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ─── HrmsTeamTable ──────────────────────────────────────────── */
function HrmsTeamTable({ rows, data, delivery, deliveryDays, setEodEmployee }) {
  return (
    <div className="Hrms-Table-Wrap">
      <table className="Hrms-Emp-Table">
        <thead>
          <tr>
            <th>Name / Joining Date</th>
            <th>Project</th>
            <th>Work</th>
            <th>Growth Roadmap</th>
            <th>EOD & Attendance</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((emp) => (
            <EmployeeRow
              key={emp.id}
              employee={emp}
              data={data}
              delivery={delivery}
              deliveryDays={deliveryDays}
              setEodEmployee={setEodEmployee}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── EmployeeRow ────────────────────────────────────────────── */
function EmployeeRow({ employee, data, delivery, deliveryDays, setEodEmployee }) {
  const [eodPopover, setEodPopover] = useState(null);

  const days       = lastDays(7);
  const projectMap = indexById(data.projects);
  const assignments = (data.teamAssignments || []).filter(
    (a) => String(a.employee) === String(employee.id),
  );
  const empProjects = assignments
    .map((a) => projectMap.get(String(a.project))?.name)
    .filter(Boolean);

  const work = delivery?.people?.[String(employee.id)] || { open_tasks: 0, open_points: 0, done_tasks: 0, done_points: 0 };

  const growthLabel = growthRungLabel(employee.department_family || "", employee.position_title, employee.growth_rung_override);

  const ic = initials(employee.display_name);
  const ac = avatarColor(employee.display_name);

  return (
    <tr className="Hrms-Emp-Row">
      {/* Name */}
      <td>
        <div className="Hrms-Name-Cell">
          <span className="Hrms-Avatar" style={{ background: ac }}>{ic}</span>
          <div className="Hrms-Name-Stack">
            <strong>{employee.display_name}</strong>
            <small className="Hrms-Join-Date">Joined: {formatDate(employee.joined_on)}</small>
          </div>
        </div>
      </td>

      {/* Projects */}
      <td>
        <div className="Hrms-Proj-Stack">
          {empProjects.length
            ? empProjects.slice(0, 3).map((name) => (
                <span key={name} className="Hrms-Proj-Tag">{name}</span>
              ))
            : <span className="Hrms-No-Proj">—</span>}
        </div>
      </td>

      {/* Work volume */}
      <td className="Hrms-Num">
        {delivery === null ? <span className="Hrms-No-Proj">…</span> : (
          <>
            <span className="Hrms-Work-Open">{work.open_tasks} open · {fmtPoints(work.open_points)} pts</span>
            <span className="Hrms-Work-Done">{work.done_tasks} done · {fmtPoints(work.done_points)} pts ({deliveryDays}d)</span>
          </>
        )}
      </td>

      {/* Growth roadmap */}
      <td>
        {growthLabel
          ? <span className="Hrms-Growth-Pill"><TrendingUp size={12} />{growthLabel}</span>
          : <span className="Hrms-No-Proj">—</span>}
      </td>

      {/* EOD & Attendance */}
      <td>
        <div className="Hrms-Att-Cell">
          <div className="Hrms-Att-Strip">
            {days.map((day) => {
              const { type, entry: ds } = getAttendanceStatus(data.dailyStatus, data.leaveRequests, employee.id, day.iso);
              const isOpen = eodPopover === day.iso;
              const attClass = type === "leave" ? "leave" : ds ? "submitted" : "missing";
              return (
                <div key={day.iso} className="Hrms-Att-Wrap" onMouseLeave={() => setEodPopover(null)}>
                  <button
                    className={`Hrms-Att-Day ${attClass}`}
                    onClick={() => setEodPopover(isOpen ? null : day.iso)}
                    onMouseEnter={() => setEodPopover(day.iso)}
                    title={day.iso}
                  >
                    {day.label}
                  </button>
                  {isOpen && (
                    <div className="Hrms-Eod-Pop">
                      <button className="Hrms-Eod-Pop-Close" onClick={() => setEodPopover(null)}>
                        <X size={13} />
                      </button>
                      <strong className="Hrms-Eod-Pop-Name">{employee.display_name}</strong>
                      <small className="Hrms-Eod-Pop-Date">{day.iso} · {type === "leave" ? "On Leave" : "EOD Report"}</small>
                      {type === "leave" ? (
                        <div className="Hrms-Eod-Pop-Project Leave">
                          <span>On Approved Leave</span>
                          <p>Employee Is On Approved Leave For This Date.</p>
                        </div>
                      ) : ds ? (
                        <div className="Hrms-Eod-Pop-Project">
                          <span>{ds.metadata?.project || "Project"}</span>
                          <p>{ds.summary || "No Summary Provided."}</p>
                        </div>
                      ) : (
                        <div className="Hrms-Eod-Pop-Project Missing">
                          <span>No EOD Submitted</span>
                          <p>Use Details To Review The Full EOD Calendar.</p>
                        </div>
                      )}
                      <div className={`Hrms-Eod-Pop-Status ${ds ? "" : type === "leave" ? "leave" : "missing"}`}>
                        <span className="dot" /> {type === "leave" ? "On Leave" : ds ? "Submitted" : "Missing"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </td>

      {/* Details */}
      <td style={{ width: 80, verticalAlign: "top" }}>
        <button className="Outline-Button" onClick={() => setEodEmployee(employee)}>
          Details
        </button>
      </td>
    </tr>
  );
}

/* ─── GrowthRungEditor ───────────────────────────────────────── */
function GrowthRungEditor({ employee, reload }) {
  const [draft, setDraft] = useState(employee.growth_rung_override || "");
  const [saving, setSaving] = useState(false);
  const family = employee.department_family || "";
  const ladder = ladderFor(family);

  if (!ladder) {
    return (
      <div className="Eod-Growth-Row">
        <strong>Growth Roadmap</strong>
        <span className="Hrms-No-Proj">{family ? `${family} has no ladder yet` : "No department family set"}</span>
      </div>
    );
  }

  const flat = ladder.key === "sales" ? ladder.rungs : ENGINEERING_FLAT;
  const currentIdx = resolveRungIndex(flat, employee.position_title, employee.growth_rung_override);
  const currentRung = flat[currentIdx];
  const dirty = draft !== (employee.growth_rung_override || "");

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, { growth_rung_override: draft });
      if (reload) await reload(["employees"]);
    } catch (e) {
      alert(e?.payload?.detail || e?.message || "Could not save the growth rung.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="Eod-Growth-Row">
      <strong>Growth Roadmap</strong>
      {currentRung && (
        <span className="Hrms-Growth-Pill"><TrendingUp size={12} />{currentRung.level} · {currentRung.title}</span>
      )}
      {employee.growth_rung_override
        ? <small className="Eod-Growth-Note">Set by hand</small>
        : <small className="Eod-Growth-Note">Auto-matched from title</small>}
      <select className="Eod-Growth-Select" value={draft} onChange={(e) => setDraft(e.target.value)}>
        <option value="">Auto — match from position title</option>
        {flat.map((r) => <option key={r.level} value={r.level}>{r.level} · {r.title}</option>)}
      </select>
      <button className="Outline-Button" disabled={!dirty || saving} onClick={save}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

/* ─── EodSummaryModal ────────────────────────────────────────── */
function EodSummaryModal({ employee, data, reload, onClose }) {
  const [tab, setTab]         = useState("calendar");
  const [calMonth, setCalMonth] = useState(new Date());
  const statuses  = (data.dailyStatus || []).filter(
    (s) => String(s.employee) === String(employee.id),
  );
  // This person's tasks straight from the server. data.tasks is one capped page
  // of the company's tasks and rarely holds a given person's work; it's only
  // the stand-in while this loads.
  const [personTasks, setPersonTasks] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPersonTasks(null);
    apiGet(`/TasksDashboard/WorkItems/?owner=${employee.id}&page_size=500`)
      .then((res) => { if (!cancelled) setPersonTasks(Array.isArray(res) ? res : (res?.results || [])); })
      .catch(() => { if (!cancelled) setPersonTasks([]); });
    return () => { cancelled = true; };
  }, [employee.id]);
  const tasks     = personTasks ?? filterForEmployee(data.tasks, employee.id);
  const monthDays = calendarDays(calMonth);
  const monthLabel = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayStr     = isoDate(new Date());
  const orderedTasks = [...tasks].sort(byBountyHighestFirst);
  const finished = orderedTasks.filter((task) => isCompleted(task.status));
  const completedTasks = finished.length;
  const pointsDone = finished.reduce((sum, task) => sum + Number(task.bounty || 0), 0);
  const pointsOpen = orderedTasks.filter((task) => !isCompleted(task.status)).reduce((sum, task) => sum + Number(task.bounty || 0), 0);

  const prevMonth = () =>
    setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1));
  const nextMonth = () =>
    setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1));

  const ic = initials(employee.display_name);
  const ac = avatarColor(employee.display_name);
  const todayStatus = findDailyStatus(statuses, employee.id, todayStr);

  return (
    <Modal onClose={onClose} wide title="">
      <div className="Eod-Modal-Hero">
        <span className="Eod-Modal-Avatar" style={{ background: ac }}>{ic}</span>
        <div>
          <h2 className="Eod-Modal-Name">{employee.display_name}</h2>
          <p className="Eod-Modal-Sub">
            {employee.department_name || "—"} · Joined {formatDate(employee.joined_on)}
          </p>
        </div>
        <div className="Eod-Modal-Today">
          {todayStatus
            ? <span className="Hrms-Pill Green"><CheckCircle size={12} /> EOD Submitted Today</span>
            : <span className="Hrms-Pill Red">No EOD Today</span>}
        </div>
      </div>

      <GrowthRungEditor employee={employee} reload={reload} />

      <div className="Eod-Kpi-Grid">
        <article className="Eod-Kpi-Card">
          <span>Open tasks</span>
          <strong>{orderedTasks.length - completedTasks}</strong>
          <small>{fmtPoints(pointsOpen)} story points still open</small>
        </article>
        <article className="Eod-Kpi-Card">
          <span>Finished tasks</span>
          <strong>{completedTasks}</strong>
          <small>All time</small>
        </article>
        <article className="Eod-Kpi-Card">
          <span>Story points finished</span>
          <strong>{fmtPoints(pointsDone)}</strong>
          <small>All time</small>
        </article>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[["calendar", "Calendar"], ["bounties", "Bounties"], ["summaries", "EOD Summaries"]]}
      />

      {/* Calendar */}
      {tab === "calendar" && (
        <div className="Eod-Cal-Wrap">
          <div className="Eod-Cal-Nav">
            <button className="Outline-Button" onClick={prevMonth}>← Previous</button>
            <h3>{monthLabel}</h3>
            <button className="Outline-Button" onClick={nextMonth}>Next →</button>
          </div>
          <div className="Eod-Cal-Grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <strong key={d} className="Eod-Cal-Hdr">{d}</strong>
            ))}
            {monthDays.map((day, i) => {
              const att = !day ? null : getAttendanceStatus(data.dailyStatus, data.leaveRequests, employee.id, day.iso);
              return !day ? (
                <span key={i} />
              ) : (
                <button
                  key={i}
                  className={`eod-cal-day ${
                    att.type === "present"
                      ? "submitted"
                      : att.type === "leave"
                      ? "leave"
                      : day.future
                      ? "future"
                      : "missing"
                  }`}
                >
                  <strong>{day.day}</strong>
                  <small>{day.iso === todayStr ? "Today" : ""}</small>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bounties */}
      {tab === "bounties" && (
        <div className="Eod-Tab-Body">
          {orderedTasks.length ? (
            <SimpleTable
              columns={["Story pts", "Task", "Project", "Status", "Progress", "Due"]}
              rows={orderedTasks.slice(0, 40).map((task) => {
                const progress = Math.round(progressForTask(task));
                return [
                  Math.round(Number(task.bounty || 0)),
                  task.title,
                  projectName(data, task.project),
                  task.status,
                  <span key={`progress-${task.id}`} className="Eod-Progress-Cell"><Progress value={progress} /><small>{progress}%</small></span>,
                  formatDate(task.due_at || task.updated_at || task.created_at),
                ];
              })}
            />
          ) : (
            <EmptyState label="No Bounties Assigned Yet." />
          )}
        </div>
      )}

      {/* EOD Summaries */}
      {tab === "summaries" && (
        <div className="Eod-Tab-Body">
          <div className="Eod-Summary-List">
            {lastDays(60).reverse().map((day) => {
              const ds = findDailyStatus(statuses, employee.id, day.iso);
              return (
                <div key={day.iso} className={`eod-summary-row ${ds ? "has" : "no"}`}>
                  <div className="Eod-Sum-Date"><strong>{day.iso}</strong></div>
                  {ds ? (
                    <div className="Eod-Sum-Content">
                      <span className="Eod-Sum-Proj">
                        {(() => {
                          const meta = ds.metadata || {};
                          const tn = meta.task_name || meta.taskName || meta.task_title || (meta.task_id && findById(data.tasks, meta.task_id)?.title);
                          const pn = meta.project_name || meta.projectName || (meta.project && (findById(data.projects, meta.project)?.name || meta.project));
                          return tn || pn || "—";
                        })()}
                      </span>
                      <p>{ds.summary || "No Summary Text."}</p>
                    </div>
                  ) : (
                    <div className="Eod-Sum-Content Empty">No EOD Submitted</div>
                  )}
                  <span className={`eod-sum-badge ${ds ? "submitted" : "missing"}`}>
                    <span className="dot" />
                    {ds ? "Submitted" : "Missing"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}
